import type { Logger } from '@core/model/domain/logger';
import { SERVICE_ID } from '@di/serviceId';
import { inject, injectable } from 'inversify';

type LogType = 'PageView' | 'StartTrackPage' | 'StopTrackPage' | 'Event' | 'Metric' | 'Exception' | 'Trace';

@injectable()
export class DefaultLogger implements Logger {
  constructor(
    @inject(SERVICE_ID.serverLogger) private readonly serverLogger: Logger,
    @inject(SERVICE_ID.env) private readonly env: 'prod' | 'preprod' | 'dev'
  ) {}

  logPageView(name?: string, url?: string): void {
    this.serverLogger.logPageView(name, url);
    this.consoleLogger('PageView', [name, url]);
  }

  startTrackPage(pageName: string): void {
    this.serverLogger.startTrackPage(pageName);
    this.consoleLogger('StartTrackPage', [pageName]);
  }

  stopTrackPage(pageName: string): void {
    this.serverLogger.stopTrackPage(pageName);
    this.consoleLogger('StartTrackPage', [pageName]);
  }

  logEvent(name: string, properties?: { [key: string]: unknown }): void {
    this.serverLogger.logEvent(name, properties);
    this.consoleLogger('Event', [name, properties]);
  }

  logMetric(name: string, average: number, properties?: { [key: string]: unknown }): void {
    this.serverLogger.logMetric(name, average, properties);
    this.consoleLogger('Metric', [name, average, properties]);
  }

  logException(exception: Error, severityLevel?: number): void {
    this.serverLogger.logException(exception, severityLevel);
    this.consoleLogger('Exception', [exception, severityLevel]);
  }

  logTrace(message: string, properties?: { [key: string]: unknown }): void {
    this.serverLogger.logTrace(message, properties);
    this.consoleLogger('Trace', [message, properties]);
  }

  private consoleLogger(logType: LogType, props: Array<unknown>) {
    if (this.env === 'prod') {
      return;
    }

    const filteredProps = props.filter((element) => element !== undefined);

    if (filteredProps.length > 0) {
      console.log(`${logType}: `, ...filteredProps);
      return;
    }

    console.log(`${logType}`);
  }
}
